import Vue from 'vue';

import { IPreviewPageDimensions, PreviewPageItem } from '@/courses/interfaces/preview-page-item.interface';
import { EduacProvider } from '@/core/providers/eduac.provider';

export class EdPreviewFilePagesProvider {
  static MaxPagesRendering = 3;

  pages: PreviewPageItem[] = Vue.observable([]);
  minPageWidth: number = 1000;
  defaultHeight: number = 180;
  queueToRender = [];
  currentPageDownloading = 0;
  totalPages = 0;

  async setPdfPagesFromUrlFile(url: string): Promise<PreviewPageItem[]> {
    
    const cachedUrl = await this.getCachedUrlFile(url);

    return new Promise((resolve , reject) => {
      let minPageWidth = this.minPageWidth;

      const pages: PreviewPageItem[] = [];  
      const loadingTask = pdfjsLib.getDocument({ url: cachedUrl });

      loadingTask.promise.then(async (pdf: any) => {
        const totalPages = pdf._pdfInfo.numPages;
        
        this.totalPages = totalPages;

        for (let i = 1; i <= totalPages; i++) { 
          this.currentPageDownloading = i;

          const page = await pdf.getPage(i);
          const sizes = this.getPageSizes(page);

          minPageWidth = sizes.width < minPageWidth ? sizes.width : minPageWidth;

          pages.push({ 
            page,
            rendered: false,
            id: (i - 1),
            size: sizes.width,
            ...sizes,
          });
        }

        this.pages = pages;
        this.minPageWidth = minPageWidth;

        resolve(pages);
      });
      loadingTask.promise.catch(() => {
        reject();
      });
    });    
  }


  async getCachedUrlFile(url:string) {
    const urlParts = url.split('/');
    const hash = urlParts[urlParts.length - 1];
    const cachedUrl = await EduacProvider.prototype.$vue.$FilesCache.getUrlFile(hash);

    if(cachedUrl) {
      return cachedUrl;
    } else {
      return new Promise((resolve, reject) => {
        const documentPDF = pdfjsLib.getDocument({ url, withCredentials: true });
        documentPDF.promise.then(async (pdf: any) => {
          pdf.getData()
              .then(async data => {
                const blob = new Blob([data], {type: 'application/pdf'});
                const urlPdf = URL.createObjectURL(blob);
                await EduacProvider.prototype.$vue.$FilesCache.cacheFile(hash, data);
                resolve(urlPdf);
              })
              .then(async (pdf: any) => {
                documentPDF.destroy();
              });
        });
      });
    }
  }

  getPageSizes(page) {
    const height = this.defaultHeight;
    const initialViewport = page.getViewport({ scale: 1 });
    const scale = (height / initialViewport.height);
    const viewport: any = page.getViewport({ scale });
    const width = viewport.width;

    return {
      width,
      height,
      scale,
    };
  }

  getPageById(id: number): PreviewPageItem {
    return this.pages.find(p => p.id === id);
  }

  hasToRenderNow(pageId: number): boolean {
    const renderingQueue = this.queueToRender.slice(0, EdPreviewFilePagesProvider.MaxPagesRendering);

    return renderingQueue.includes(pageId);
  }

  isPageInQueueToRender(pageId: number): boolean {
    return this.queueToRender.includes(pageId);
  }

  addToQueueToRender(pageId: number) {
    if (!this.queueToRender.includes(pageId)) {
      this.queueToRender.push(pageId);
    }
  }

  removeFromQueueToRender(pageId: number) {
    const index = this.queueToRender.indexOf(pageId);

    if (index !== -1) {
      this.queueToRender.splice(index, 1);
    }
  }

  isPageRendered(pageId: number): boolean {
    const page = this.getPageById(pageId);

    return page ? page.rendered : false;
  }

  setPageRendered(pageId: number, image: string) {
    const page = this.getPageById(pageId);

    if (page) {
      page.rendered = true;
      page.image = image;

      this.removeFromQueueToRender(pageId);
    }
  }

  getPageImage(pageId: number): string {
    const page = this.getPageById(pageId);

    return page ? page.image : '';
  }

  rightScale({ cont, img }: { cont: IPreviewPageDimensions, img: IPreviewPageDimensions }) {
    const scales = [cont.clientWidth / img.clientHeight, cont.clientHeight / img.clientWidth];

    if (scales[0] * img.clientHeight <= cont.clientWidth && scales[0] * img.clientWidth <= cont.clientHeight) {
      return scales[0];
    }

    return scales[1];
  }
}
